import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, FormControl, Select, MenuItem, TextField, Box } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import AddIcon from '@material-ui/icons/Add';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PageNavigator from './PageNavigator';
import PlacementQueue from './PlacementQueue';
import MeasurementSummary from './MeasurementSummary';

const useStyles = makeStyles(() => ({
  sidebar: {
    width: 380,
    minWidth: 380,
    background: '#ffffff',
    borderLeft: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    height: '100%',
    overflow: 'hidden',
  },
  queueWrap: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  queueHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px 2px',
    flexShrink: 0,
  },
  queueTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#aaa',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  queueCount: {
    fontSize: 11,
    color: '#ccc',
  },
  saveBar: {
    padding: '10px 14px',
    borderTop: '1px solid #e0e0e0',
    background: '#fafafa',
    flexShrink: 0,
  },
  saveBtn: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: 14,
  },
}));

/**
 * PlanTakeoffSidebar — the full right-hand sidebar for the Plan Takeoff workspace.
 *
 * Layout (top to bottom):
 *   1. PageNavigator   — scrollable page thumbnail strip
 *   2. PlacementQueue  — grouped, searchable list of plan items (scrollable)
 *   3. MeasurementSummary — progress + totals footer
 *   4. Save button
 *
 * Props:
 *   pages          – [{ page, url, title }]
 *   activePage     – currently displayed page number
 *   onSelectPage   – (page) => void
 *   groupedItems   – from usePlanItems().groupedItems
 *   activeItemId   – from usePlanItems().activeItemId
 *   onSelectItem   – (itemId) => void
 *   onDrawItem     – (itemId, reset?) => void
 *   onSkipItem     – (itemId) => void
 *   onChangeType   – (itemId, newType) => void
 *   summary        – from usePlanItems().summary
 *   saving         – bool
 *   onSave         – () => void
 */
const PlanTakeoffSidebar = ({
  pages = [],
  activePage,
  onSelectPage,
  groupedItems = [],
  activeItemId,
  activePlacementId,
  onSelectItem,
  onSkipItem,
  onChangeType,
  summary,
  saving,
  onSave,
  importingAssembly,
  onImportToAssembly,
  cropBaseUrl,
  onPickSymbol,
  onFindOnPage,
  onOpenSymbolWizard,
  expandedItems,
  onToggleExpand,
  onEditPlacement,
  onDeletePlacement,
  onAddPlacement,
  onConfirmEdit,
  onCancelEdit,
  editingPlacement,
  buildings = [],
  activeBuilding,
  onSetActiveBuilding,
  onAddBuilding,
  completedAreas = [],
  onToggleAreaComplete,
}) => {
  const classes = useStyles();
  const [addingBuilding, setAddingBuilding] = useState(false);
  const [newBuildingName, setNewBuildingName] = useState('');

  const totalItems = groupedItems.reduce((sum, g) => sum + g.items.length, 0);
  const totalPlaced = summary ? summary.totalPlaced : 0;

  const saveBtnLabel = saving ? 'Saving…' : 'Save Takeoff';

  const handleAddBuilding = () => {
    if (newBuildingName.trim() && onAddBuilding) {
      onAddBuilding(newBuildingName.trim());
      setNewBuildingName('');
      setAddingBuilding(false);
    }
  };

  return (
    <div className={classes.sidebar}>
      {/* 1. Page navigator */}
      <PageNavigator
        pages={pages}
        activePage={activePage}
        onSelectPage={onSelectPage}
      />

      {/* Building/Area selector */}
      <Box px={1.5} py={1} style={{ borderBottom: '1px solid #f0f0f0' }}>
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          <span style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Area</span>
          <FormControl size="small" variant="outlined" style={{ flex: 1 }}>
            <Select
              value={activeBuilding || ''}
              onChange={(e) => onSetActiveBuilding(e.target.value || null)}
              displayEmpty
              style={{ fontSize: 12 }}
            >
              <MenuItem value="" style={{ fontSize: 12, color: '#999' }}>All / Unassigned</MenuItem>
              {buildings.map(b => {
                const bName = b.name || b;
                const isComplete = completedAreas.includes(bName);
                return (
                  <MenuItem key={bName} value={bName} style={{ fontSize: 12 }}>
                    <Box display="flex" alignItems="center" style={{ gap: 6, width: '100%' }}>
                      {isComplete && <CheckCircleIcon style={{ fontSize: 14, color: '#4caf50' }} />}
                      <span style={isComplete ? { color: '#4caf50', fontWeight: 500 } : undefined}>{bName}</span>
                      {isComplete && <span style={{ fontSize: 10, color: '#999', marginLeft: 'auto' }}>Done</span>}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Button
            size="small"
            style={{ minWidth: 0, padding: '4px 6px' }}
            onClick={() => setAddingBuilding(!addingBuilding)}
          >
            <AddIcon style={{ fontSize: 16 }} />
          </Button>
        </Box>
        {activeBuilding && onToggleAreaComplete && (
          <Box mt={0.5}>
            {completedAreas.includes(activeBuilding) ? (
              <Button
                fullWidth
                size="small"
                variant="text"
                startIcon={<CheckCircleIcon style={{ fontSize: 16, color: '#4caf50' }} />}
                onClick={() => onToggleAreaComplete(activeBuilding)}
                style={{ textTransform: 'none', fontSize: 11, color: '#4caf50', justifyContent: 'flex-start' }}
              >
                {activeBuilding} — Completed (click to reopen)
              </Button>
            ) : (
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<CheckCircleOutlineIcon style={{ fontSize: 16 }} />}
                onClick={() => onToggleAreaComplete(activeBuilding)}
                style={{ textTransform: 'none', fontSize: 11, justifyContent: 'flex-start' }}
              >
                Mark "{activeBuilding}" as complete
              </Button>
            )}
          </Box>
        )}
        {addingBuilding && (
          <Box display="flex" alignItems="center" style={{ gap: 4, marginTop: 4 }}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="e.g. Area A, Building 1"
              value={newBuildingName}
              onChange={(e) => setNewBuildingName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddBuilding(); }}
              style={{ flex: 1 }}
              inputProps={{ style: { fontSize: 12, padding: '6px 8px' } }}
              autoFocus
            />
            <Button size="small" variant="contained" color="primary" onClick={handleAddBuilding} style={{ minWidth: 0, padding: '4px 8px', fontSize: 11 }}>
              Add
            </Button>
          </Box>
        )}
      </Box>

      {/* 2. Placement queue */}
      <div className={classes.queueWrap}>
        {totalItems > 0 && (
          <div className={classes.queueHeader}>
            <span className={classes.queueTitle}>Placement Queue</span>
            {onOpenSymbolWizard && (
              <Button
                size="small"
                onClick={onOpenSymbolWizard}
                style={{ textTransform: 'none', fontSize: 10, padding: '2px 8px', minWidth: 0 }}
              >
                Assign Symbols
              </Button>
            )}
            <span className={classes.queueCount}>{totalPlaced}/{totalItems}</span>
          </div>
        )}
        <PlacementQueue
          groupedItems={groupedItems}
          activeItemId={activeItemId}
          activePlacementId={activePlacementId}
          onSelectItem={onSelectItem}
          onSkipItem={onSkipItem}
          onChangeType={onChangeType}
          cropBaseUrl={cropBaseUrl}
          onPickSymbol={onPickSymbol}
          onFindOnPage={onFindOnPage}
          expandedItems={expandedItems}
          onToggleExpand={onToggleExpand}
          onEditPlacement={onEditPlacement}
          onDeletePlacement={onDeletePlacement}
          onAddPlacement={onAddPlacement}
          onConfirmEdit={onConfirmEdit}
          onCancelEdit={onCancelEdit}
          editingPlacement={editingPlacement}
        />
      </div>

      {/* 3. Measurement summary */}
      <MeasurementSummary summary={summary} />

      {/* 4. Save button */}
      <div className={classes.saveBar}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="small"
          className={classes.saveBtn}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
          disabled={saving}
        >
          {saveBtnLabel}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          size="small"
          className={classes.saveBtn}
          style={{ marginTop: 8 }}
          onClick={onImportToAssembly}
          disabled={saving || importingAssembly || totalPlaced === 0}
        >
          {importingAssembly ? 'Sending to Assembly...' : 'Save & Send to Assembly'}
        </Button>
      </div>
    </div>
  );
};

export default PlanTakeoffSidebar;
